import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { getRems } from '@blueprint/utils';
import { Heading3, Text, FieldMessage } from '@blueprint/ui';

export type RadioButtonActiveColors = 'white' | 'grey-slate';
export type RadioButtonActiveBackgroundColors =
    | 'function-error'
    | 'function-success'
    | 'crimson'
    | 'grass';

type RadioButtonLabelProps = {
    errorMessage?: string;
};

type RadioButtonInputProps = {
    activeTextColor: RadioButtonActiveColors;
    activeBackgroundColor: RadioButtonActiveBackgroundColors;
};

export const RadioGroupFieldset = styled('fieldset')(
    () => css`
        border: 0;
        padding: 0;
        margin: 0;
    `
);

export const RadioGroupLegend = styled(Heading3)(
    () => css`
        margin-bottom: ${getRems(16)};
    `
);

export const RadioGroupDescription = styled(Text)(
    () => css`
        margin-bottom: ${getRems(24)};
    `
);

export const RadioGroupContainer = styled('div')(
    () => css`
        display: flex;
        margin-left: -${getRems(7.5)};
        margin-right: -${getRems(7.5)};
    `
);

export const RadioButtonInput = styled('input')<RadioButtonInputProps>(
    ({ theme, activeTextColor, activeBackgroundColor }) => css`
        display: block;
        margin: 0;
        width: 0;
        height: 0;
        opacity: 0;

        &:checked + label,
        &:checked + label:hover {
            background-color: ${theme.colors[activeBackgroundColor]};
            box-shadow: none;
            color: ${theme.colors[activeTextColor]};
            transform: none;
        }

        &:focus:checked + label,
        &:focus + label {
            box-shadow: inset 0 0 0 ${getRems(1)}
                    ${theme.buttons.secondary.borderColor},
                ${theme.focusRing};
        }
    `
);

/**
 * Styled like a secondary button without the filled color
 */
export const RadioButtonLabel = styled('label')<RadioButtonLabelProps>(
    ({ theme, errorMessage }) => css`
        cursor: pointer;
        color: ${theme.buttons.secondary.color};
        display: block;
        width: 100%;
        box-shadow: inset 0 0 0 ${getRems(1)}
            ${theme.buttons.secondary.borderColor};
        height: ${getRems(45)};
        line-height: ${getRems(42.5)};
        font-size: ${theme.fontSizes[2]};
        font-style: normal;
        font-weight: ${theme.fontWeight[400]};
        text-align: center;
        border-radius: ${theme.borderRadius['4px']};
        background-color: ${theme.buttons.secondary.backgroundColor};
        margin-left: ${getRems(7.5)};
        margin-right: ${getRems(7.5)};
        transition: 0.2s ease-in-out, background 0.05s linear,
            box-shadow 0.2s linear;

        ${errorMessage &&
        `
            box-shadow: inset 0 0 0 ${getRems(1)} ${theme.colors.crimson};
        `}

        &:hover:not(:active) {
            transform: translateY(-1px);
            box-shadow: inset 0 0 0 ${getRems(1)}
                    ${theme.buttons.secondary.borderColor},
                0px 0px 0.75rem rgba(50, 39, 68, 0.08),
                0px 0px 1px rgba(50, 39, 68, 0.32),
                0px ${theme.spacing['8px']} ${theme.spacing['16px']} -${theme
                        .spacing['8px']} rgba(50, 39, 68, 0.8);
        }
    `
);

export const RadioGroupErrorMessage = styled(FieldMessage)(
    ({ theme }) => css`
        margin-top: ${theme.spacing['16px']};
    `
);
